/**
 * Document Operations API - Core Types
 *
 * These types define the operation schema for AI agents to edit TipTap documents.
 * The core API is generic and works for any document type.
 */

// ===========================================
// NODE TARGETING
// ===========================================

/**
 * Target a specific node by its UUID
 */
export interface NodeTarget {
  nodeId: string;
}

// ===========================================
// NODE TYPES
// ===========================================

/**
 * Block node types supported by the operations API
 */
export type NodeType =
  | "paragraph"
  | "heading"
  | "bulletList"
  | "orderedList"
  | "listItem"
  | "blockquote"
  | "codeBlock"
  | "table"
  | "pageBreak"
  | "horizontalRule";

/**
 * Attributes that can be set on nodes
 */
export interface NodeAttrs {
  level?: number; // For headings: 1-6
  textAlign?: "left" | "center" | "right" | "justify";
  [key: string]: any; // Allow custom attributes
}

// ===========================================
// TEXT MARKS (Inline Formatting)
// ===========================================

/**
 * Mark types for inline text formatting
 * These are applied to text content, not to block nodes
 */
export type MarkType =
  | "bold"
  | "italic"
  | "underline"
  | "strike"
  | "code"
  | "subscript"
  | "superscript";

/**
 * A text segment with optional formatting marks
 */
export interface FormattedTextSegment {
  text: string;
  marks?: MarkType[];
}

/**
 * Content can be plain string OR array of formatted segments
 */
export type ContentType = string | FormattedTextSegment[];

// ===========================================
// CORE OPERATIONS
// ===========================================

/**
 * Insert a new node before or after an existing node
 */
export interface InsertOperation {
  op: "insert";
  target: NodeTarget;
  position: "before" | "after";
  nodeType: NodeType;
  content: ContentType; // Plain text OR formatted segments
  attrs?: NodeAttrs;
  newNodeId?: string; // Optional: pre-generated node ID (if not provided, frontend generates one)
  tableData?: string[][]; // 2D array for table creation (first row = headers)
}

/**
 * Replace the content of an existing node
 */
export interface ReplaceOperation {
  op: "replace";
  target: NodeTarget;
  content: ContentType; // Plain text OR formatted segments
}

/**
 * Delete an existing node
 */
export interface DeleteOperation {
  op: "delete";
  target: NodeTarget;
}

/**
 * Append a new node at the end of the document
 */
export interface AppendOperation {
  op: "append";
  nodeType: NodeType;
  content: ContentType; // Plain text OR formatted segments
  attrs?: NodeAttrs;
  newNodeId?: string; // Optional: pre-generated node ID (if not provided, frontend generates one)
  tableData?: string[][]; // 2D array for table creation (first row = headers)
}

/**
 * Add a comment to a node
 */
export interface CommentOperation {
  op: "comment";
  target: NodeTarget;
  commentContent: string; // The comment text
  author?: string; // Comment author (defaults to "AI")
}

/**
 * Edit a node's content with word-level diff tracking
 * This operation computes the diff between old and new content
 * and applies granular changes for proper track changes display
 */
export interface EditOperation {
  op: "edit";
  target: NodeTarget;
  newContent: string; // The full new content (diff computed automatically)
}

/**
 * Move a node from its current position to before/after another node.
 * Preserves the node's content, formatting, and inline marks.
 */
export interface MoveOperation {
  op: "move";
  target: NodeTarget; // The node to move
  destination: NodeTarget; // The reference node for placement
  position: "before" | "after"; // Place before or after the destination node
}

/**
 * Update a node's attributes without changing its content.
 * Use for heading level changes, text alignment, indentation, etc.
 */
export interface SetAttrsOperation {
  op: "set_attrs";
  target: NodeTarget;
  attrs: Record<string, any>;
}

// ===========================================
// TRACK CHANGE MANAGEMENT OPERATIONS
// ===========================================

/**
 * Accept a specific tracked change (makes insertion permanent or removes deletion)
 */
export interface AcceptChangeOperation {
  op: "accept_change";
  changeId: string;
}

/**
 * Reject a specific tracked change (removes insertion or restores deletion)
 */
export interface RejectChangeOperation {
  op: "reject_change";
  changeId: string;
}

/**
 * Accept all tracked changes in the document
 */
export interface AcceptAllChangesOperation {
  op: "accept_all_changes";
}

/**
 * Reject all tracked changes in the document
 */
export interface RejectAllChangesOperation {
  op: "reject_all_changes";
}

// ===========================================
// COMMENT MANAGEMENT OPERATIONS
// ===========================================

/**
 * Remove a comment from the document
 */
export interface RemoveCommentOperation {
  op: "remove_comment";
  commentId: string;
}

/**
 * Reply to an existing comment
 */
export interface ReplyCommentOperation {
  op: "reply_comment";
  commentId: string;
  replyContent: string;
  author?: string;
}

/**
 * Union of all core operation types
 */
export type CoreOperation =
  | InsertOperation
  | ReplaceOperation
  | DeleteOperation
  | AppendOperation
  | CommentOperation
  | EditOperation
  | MoveOperation
  | SetAttrsOperation
  | AcceptChangeOperation
  | RejectChangeOperation
  | AcceptAllChangesOperation
  | RejectAllChangesOperation
  | RemoveCommentOperation
  | ReplyCommentOperation;

// ===========================================
// OPERATION PATCH
// ===========================================

/**
 * Metadata about the operation source
 */
export interface OperationMetadata {
  agentId?: string; // Which AI agent generated this
  toolName?: string; // Which tool was called
  timestamp?: string; // ISO timestamp
  callId?: string; // Unique call identifier
}

/**
 * A patch containing one or more operations to apply
 */
export interface OperationPatch {
  operations: CoreOperation[];
  metadata?: OperationMetadata;
}

// ===========================================
// EXECUTION RESULTS
// ===========================================

/**
 * Comment data returned when a comment operation succeeds
 */
export interface CommentData {
  id: string;
  content: string;
  author: string;
  createdAt: Date;
}

/**
 * Result of executing a single operation
 */
export interface ExecutionResult {
  success: boolean;
  operationIndex: number; // Index in the operations array
  nodeId?: string; // ID of created/modified node (for insert/append)
  commentData?: CommentData; // Comment data (for comment operations)
  error?: string; // Error message if failed
}

/**
 * Result of executing an entire patch
 */
export interface PatchExecutionResult {
  success: boolean; // True if all operations succeeded
  results: ExecutionResult[];
  failedCount: number;
  comments: CommentData[]; // All comments created by this patch
}

// ===========================================
// SERIALIZED DOCUMENT (for LLM context)
// ===========================================

/**
 * Comment mark data extracted from a node
 */
export interface SerializedComment {
  commentId: string; // Unique ID of the comment
  text: string; // The text that has the comment mark
}

/**
 * Track change mark data extracted from a node
 */
export interface SerializedTrackChange {
  changeId: string; // Unique ID of the change
  type: "insertion" | "deletion"; // Type of change
  text: string; // The text that was inserted or marked for deletion
  author?: string; // Who made the change
  date?: string; // When the change was made
}

/**
 * A simplified representation of a document node for LLM context
 */
export interface SerializedNode {
  id: string; // UUID of the node
  type: string; // Node type: "paragraph", "heading", etc.
  content: string; // Plain text content
  attrs?: NodeAttrs; // Node attributes (level for headings, etc.)
  comments?: SerializedComment[]; // Comments attached to text in this node
  trackChanges?: SerializedTrackChange[]; // Track changes (insertions/deletions) in this node
}

// ===========================================
// TYPE GUARDS
// ===========================================

export function isInsertOperation(op: CoreOperation): op is InsertOperation {
  return op.op === "insert";
}

export function isReplaceOperation(op: CoreOperation): op is ReplaceOperation {
  return op.op === "replace";
}

export function isDeleteOperation(op: CoreOperation): op is DeleteOperation {
  return op.op === "delete";
}

export function isAppendOperation(op: CoreOperation): op is AppendOperation {
  return op.op === "append";
}

export function isCommentOperation(op: CoreOperation): op is CommentOperation {
  return op.op === "comment";
}

export function isEditOperation(op: CoreOperation): op is EditOperation {
  return op.op === "edit";
}

export function isMoveOperation(op: CoreOperation): op is MoveOperation {
  return op.op === "move";
}

export function isSetAttrsOperation(
  op: CoreOperation
): op is SetAttrsOperation {
  return op.op === "set_attrs";
}

export function isAcceptChangeOperation(
  op: CoreOperation
): op is AcceptChangeOperation {
  return op.op === "accept_change";
}

export function isRejectChangeOperation(
  op: CoreOperation
): op is RejectChangeOperation {
  return op.op === "reject_change";
}

export function isAcceptAllChangesOperation(
  op: CoreOperation
): op is AcceptAllChangesOperation {
  return op.op === "accept_all_changes";
}

export function isRejectAllChangesOperation(
  op: CoreOperation
): op is RejectAllChangesOperation {
  return op.op === "reject_all_changes";
}

export function isRemoveCommentOperation(
  op: CoreOperation
): op is RemoveCommentOperation {
  return op.op === "remove_comment";
}

export function isReplyCommentOperation(
  op: CoreOperation
): op is ReplyCommentOperation {
  return op.op === "reply_comment";
}
